import download from "../../../../util/download";
import { DetailsDataItem } from "../../utils/sortUtils";
import { tienFormat } from "../../utils/NumberUtils";
import { getCachedDetailsReportData, getDetailsReportDisplayHtml } from "./detailsReport";
import { buildReportHtmlDocument } from "./reportHtmlDocument";
import { getTotalReportDisplayHtml } from "./totalReportByMessage";

type TotalDisplayTemplete = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | undefined;

export type ExportTotalReportHtmlOptions = {
  totalDisplayTemplete?: TotalDisplayTemplete;
  showDetails?: boolean;
  renderTienCo?: boolean;
  filename?: string;
};
export type ExportBaMienTotalReportHtmlOptions = ExportTotalReportHtmlOptions;

export type ExportDetailsReportHtmlOptions = {
  data?: DetailsDataItem[];
  filename?: string;
};

export type ExportBothReportsHtmlOptions = {
  totalDisplayTemplete?: TotalDisplayTemplete;
  showDetails?: boolean;
  renderTienCo?: boolean;
  totalFilename?: string;
  detailsFilename?: string;
  detailsData?: DetailsDataItem[];
};

function sanitizeFilenamePart(value: string): string {
  return value
    .trim()
    .replace(/[\\/:*?"<>|]/g, "-")
    .replace(/\s+/g, "_");
}

function formatDateForFilename(dateValue: any): string {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) {
    return new Date().toISOString().slice(0, 10);
  }
  const yyyy = date.getFullYear();
  const mm = `${date.getMonth() + 1}`.padStart(2, "0");
  const dd = `${date.getDate()}`.padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function buildDefaultBaseName(reportData: any, fallbackName: string): string {
  const contactName = sanitizeFilenamePart(String(reportData?.contactName || "contact"));
  const datePart = formatDateForFilename(reportData?.date);
  const messageId = reportData?.messageId ?? "all";
  return `${fallbackName}_${datePart}_${contactName}_${messageId}`;
}

function triggerHtmlDownload(html: string, filenameWithoutExt: string): string {
  const fileName = `${sanitizeFilenamePart(filenameWithoutExt)}.html`;
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const blobUrl = URL.createObjectURL(blob);
  download(blobUrl, fileName);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  return fileName;
}

async function tryShareHtmlFileOrDownload(html: string, filenameWithoutExt: string): Promise<string> {
  const fileName = `${sanitizeFilenamePart(filenameWithoutExt)}.html`;
  try {
    const nav = navigator as any;
    if (nav && typeof nav.share === "function") {
      const file = new File([html], fileName, { type: "text/html" });
      if (!nav.canShare || nav.canShare({ files: [file] })) {
        await nav.share({
          title: fileName,
          text: "Báo cáo HTML",
          files: [file],
        });
        return fileName;
      }
    }
  } catch (err) {
    // Fallback xuống download nếu share thất bại.
  }

  triggerHtmlDownload(html, filenameWithoutExt);
  return fileName;
}

export async function writeTotalReportHtmlFile(
  totalData: any,
  options?: ExportTotalReportHtmlOptions,
): Promise<string> {
  const html = getTotalReportDisplayHtml(totalData, {
    totalDisplayTemplete: options?.totalDisplayTemplete,
    // Export mặc định gồm cả group + detail để phản ánh đầy đủ báo cáo.
    showDetails: options?.showDetails ?? true,
    renderTienCo: options?.renderTienCo,
  });
  const fileBaseName = options?.filename || buildDefaultBaseName(totalData, "total_report");
  return tryShareHtmlFileOrDownload(html, fileBaseName);
}

export async function writeBaMienTotalReportHtmlFile(
  totalMessages: any[],
  options?: ExportBaMienTotalReportHtmlOptions,
): Promise<string> {
  const firstThreeMessages = (totalMessages || []).slice(0, 3);
  const sections = firstThreeMessages.map((msg, index) => {
    const fragment = getTotalReportDisplayHtml(msg, {
      totalDisplayTemplete: options?.totalDisplayTemplete,
      showDetails: options?.showDetails ?? true,
      renderTienCo: options?.renderTienCo,
      wrapDocument: false,
    });
    return `<div class="report-section">${fragment}</div>`;
  });
  let total = 0;
  let totalText = "";
  firstThreeMessages.forEach((msg: any) => {
    total += Number(msg?.laiLoSauHoi || 0);
    totalText += totalText == "" ? (msg?.mienText || "") : (" + " + (msg?.mienText || ""));
  });
  const totalLabel = total > 0 ? "Lãi: " : (total < 0 ? "Lỗ: " : "");
  const summaryHtml = `
    <div class="report-section">
      <div>${totalText}</div>
      <div class="basic-lailo ${total < 0 ? "lo" : "lai"}">${totalLabel}${tienFormat(total, 1)}</div>
    </div>
  `;
  const html = buildReportHtmlDocument(`${sections.join("")}${summaryHtml}`, "Total Report - Ba Mien");
  const fileBaseName =
    options?.filename || buildDefaultBaseName(firstThreeMessages[0] || {}, "total_report_3_mien");
  return tryShareHtmlFileOrDownload(html, fileBaseName);
}

export async function writeDetailsReportHtmlFile(
  message: any,
  options?: ExportDetailsReportHtmlOptions,
): Promise<string> {
  const detailsData = options?.data ?? getCachedDetailsReportData(message);
  const html = await getDetailsReportDisplayHtml(message, detailsData);
  const fileBaseName = options?.filename || buildDefaultBaseName(message, "details_report");
  return tryShareHtmlFileOrDownload(html, fileBaseName);
}

export async function writeBothReportsHtmlFiles(
  totalData: any,
  message: any,
  options?: ExportBothReportsHtmlOptions,
): Promise<{ totalFile: string; detailsFile: string }> {
  const totalFile = await writeTotalReportHtmlFile(totalData, {
    totalDisplayTemplete: options?.totalDisplayTemplete,
    showDetails: options?.showDetails,
    renderTienCo: options?.renderTienCo,
    filename: options?.totalFilename,
  });
  const detailsFile = await writeDetailsReportHtmlFile(message, {
    data: options?.detailsData,
    filename: options?.detailsFilename,
  });
  return { totalFile, detailsFile };
}
